class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.storageAvailable = this.checkStorageAvailability();
    this.wishlistItems = this.getWishlist();
    this.init();
  }

  /**
   * Check if localStorage is available and accessible
   * Returns false in sandboxed contexts (like theme editor preview)
   */
  checkStorageAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      console.warn('⚠️ localStorage is not available for wishlist:', error.message);
      return false;
    }
  }

  init() {
    // Initialize wishlist buttons
    this.updateAllWishlistButtons();
    this.updateWishlistCount();

    // Add event listeners with capture phase to ensure we catch the event first
    document.addEventListener(
      'click',
      (e) => {
        const wishlistBtn = e.target.closest('.wishlist-btn');
        if (wishlistBtn) {
          // Stop the event immediately
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const productId = wishlistBtn.dataset.productId;
          const productTitle = wishlistBtn.dataset.productTitle;
          const productUrl = wishlistBtn.dataset.productUrl;
          const productImage = wishlistBtn.dataset.productImage;
          const productPrice = wishlistBtn.dataset.productPrice;

          this.toggleWishlist({
            id: productId,
            title: productTitle,
            url: productUrl,
            image: productImage,
            price: productPrice,
          });

          return false;
        }
      },
      true
    ); // Use capture phase
  }

  getWishlist() {
    if (!this.storageAvailable) {
      return [];
    }

    try {
      const wishlist = localStorage.getItem(this.storageKey);
      return wishlist ? JSON.parse(wishlist) : [];
    } catch (error) {
      console.error('Error reading wishlist:', error);
      return [];
    }
  }

  saveWishlist() {
    if (!this.storageAvailable) {
      console.warn('⚠️ Cannot save wishlist - localStorage not available (sandboxed context)');
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlistItems));
      this.updateAllWishlistButtons();
      this.updateWishlistCount();

      // Trigger custom event for wishlist page to update
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  toggleWishlist(product) {
    const existingIndex = this.wishlistItems.findIndex((item) => item.id === product.id);

    if (existingIndex > -1) {
      // Remove from wishlist
      this.wishlistItems.splice(existingIndex, 1);
      this.showNotification(`${product.title} removed from wishlist`);
    } else {
      // Add to wishlist
      this.wishlistItems.push(product);
      this.showNotification(`${product.title} added to wishlist`);
    }

    this.saveWishlist();
  }

  isInWishlist(productId) {
    return this.wishlistItems.some((item) => item.id === productId);
  }

  removeFromWishlist(productId) {
    const index = this.wishlistItems.findIndex((item) => item.id === productId);
    if (index > -1) {
      const product = this.wishlistItems[index];
      this.wishlistItems.splice(index, 1);
      this.saveWishlist();
      this.showNotification(`${product.title} removed from wishlist`);
    }
  }

  updateAllWishlistButtons() {
    document.querySelectorAll('.wishlist-btn').forEach((btn) => {
      const productId = btn.dataset.productId;
      const isInWishlist = this.isInWishlist(productId);

      if (isInWishlist) {
        btn.classList.add('active');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-label', 'Add to wishlist');
      }
    });
  }

  updateWishlistCount() {
    const count = this.wishlistItems.length;
    const countElements = document.querySelectorAll('.wishlist-count');

    countElements.forEach((el) => {
      if (count > 0) {
        el.textContent = count;
        el.style.display = 'flex';
      } else {
        el.style.display = 'none';
      }
    });
  }

  showNotification(message) {
    // Remove existing notification if any
    const existingNotification = document.querySelector('.wishlist-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create notification
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    document.body.appendChild(notification);

    // Trigger animation
    setTimeout(() => {
      notification.classList.add('show');
    }, 10);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }

  getAllItems() {
    return this.wishlistItems;
  }
}

// Initialize wishlist manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}
